/**
 * What the menu capture accepts and how much of it (plan §10.3, D12). Pure:
 * the browser-only processing lives in `src/lib/menu-capture/`.
 */

/** Limits of one read (the API enforces the same ones, §5.4 `limits`). */
export const PHOTOS_PER_READ = 12;
export const PAGES_PER_READ = 20;
/**
 * Images of one read: the parts of very tall pages count here, not as pages
 * (a page split in parts is one page for the owner).
 */
export const IMAGES_PER_READ = 60;
export const TEXT_CHARS_LIMIT = 20000;

export type TCaptureFileKind =
  | 'photo'
  | 'heic'
  | 'pdf'
  | 'sheet'
  | 'unsupported';

const HEIC_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);
const HEIC_EXTENSIONS = new Set(['heic', 'heif']);
const PDF_TYPES = new Set(['application/pdf']);
const PDF_EXTENSIONS = new Set(['pdf']);
const SHEET_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.oasis.opendocument.spreadsheet',
  'text/csv',
]);
const SHEET_EXTENSIONS = new Set(['xlsx', 'xls', 'ods', 'csv']);
const PHOTO_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);
const PHOTO_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp']);

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

/**
 * The kind of a picked file, by its MIME type or, when the browser reports
 * none or another one, by its extension (Android pickers often send an empty
 * type; Windows reports `.csv` as an Excel type).
 */
export function classifyFile(file: {
  name: string;
  type: string;
}): TCaptureFileKind {
  const type = (file.type ?? '').toLowerCase();
  const extension = extensionOf(file.name ?? '');
  if (HEIC_TYPES.has(type) || HEIC_EXTENSIONS.has(extension)) return 'heic';
  if (PDF_TYPES.has(type) || PDF_EXTENSIONS.has(extension)) return 'pdf';
  if (SHEET_TYPES.has(type) || SHEET_EXTENSIONS.has(extension)) return 'sheet';
  if (PHOTO_TYPES.has(type) || PHOTO_EXTENSIONS.has(extension)) return 'photo';
  return 'unsupported';
}

/** A CSV file (read as text, not as a workbook), by MIME type or extension. */
export function isCsvFile(file: { name: string; type: string }): boolean {
  return (
    (file.type ?? '').toLowerCase() === 'text/csv' ||
    extensionOf(file.name ?? '') === 'csv'
  );
}

/**
 * Windows-1252 bytes 0x80-0x9F (€, curly quotes, dashes…); every other byte
 * is its Latin-1 character. Mapped here because some runtimes' TextDecoder
 * reads the whole code page as Latin-1 (Node 22 turns 0x80 into U+0080).
 */
const WINDOWS_1252_HIGH =
  '€\u0081‚ƒ„…†‡ˆ‰Š‹Œ\u008DŽ\u008F\u0090‘’“”•–—˜™š›œ\u009DžŸ';

function decodeWindows1252(bytes: Uint8Array): string {
  let text = '';
  for (const byte of bytes) {
    text +=
      byte >= 0x80 && byte <= 0x9f
        ? WINDOWS_1252_HIGH[byte - 0x80]
        : String.fromCharCode(byte);
  }
  return text;
}

/**
 * The text of a CSV file. Spreadsheet apps save CSVs as UTF-8 (with or
 * without a BOM), as UTF-16 with a BOM, or in the Windows code page (Excel in
 * Spanish, «Café» and «€» as single bytes): a UTF-16 BOM decides, else strict
 * UTF-8, else Windows-1252. Byte order marks are dropped (also a UTF-8 one
 * before a file that is not UTF-8 after all).
 */
export function decodeCsvBytes(bytes: Uint8Array): string {
  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder('utf-16le').decode(bytes);
  }
  if (bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder('utf-16be').decode(bytes);
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    const utf8Bom = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf;
    return decodeWindows1252(utf8Bom ? bytes.subarray(3) : bytes);
  }
}

/** The separators a CSV may use; a tie goes to the first (`;` of Excel in Spanish, whose decimals are commas). */
const CSV_SEPARATORS = [';', '\t', ','];
/** Lines looked at to find the separator, within its first characters. */
const CSV_SNIFF_LINES = 20;
const CSV_SNIFF_CHARS = 65536;

/** How many times `separator` splits each of the first lines, outside quotes. */
function separatorCounts(text: string, separator: string): number[] {
  const counts: number[] = [];
  let count = 0;
  let quoted = false;
  for (const char of text.slice(0, CSV_SNIFF_CHARS)) {
    if (char === '"') quoted = !quoted;
    else if (!quoted && char === '\n') {
      counts.push(count);
      count = 0;
      if (counts.length >= CSV_SNIFF_LINES) return counts;
    } else if (!quoted && char === separator) count += 1;
  }
  counts.push(count);
  return counts;
}

/**
 * How well `separator` splits the lines into columns: the lines that have
 * its most common non-zero count («Empanada;2,50» splits the same way on
 * every line; the commas of decimals and of descriptions do not).
 */
function separatorScore(text: string, separator: string): number {
  const counts = separatorCounts(text, separator);
  const lines = new Map<number, number>();
  for (const count of counts) {
    if (count > 0) lines.set(count, (lines.get(count) ?? 0) + 1);
  }
  return Math.max(0, ...lines.values());
}

/**
 * The CSV text with its separator declared on a first `sep=` line (Excel's
 * own hint), unless it has one already, so SheetJS always parses it as
 * delimited text: otherwise it sniffs the first characters and sends some
 * CSVs to other formats (a first column named «ID» is read as SYLK and loses
 * its accents). The separator is the one of semicolon, tab and comma that
 * splits the most of the first lines into the same number of columns.
 */
export function csvWithSeparator(text: string): string {
  if (text.startsWith('sep=')) return text;
  const separator = CSV_SEPARATORS.reduce((best, candidate) =>
    separatorScore(text, candidate) > separatorScore(text, best)
      ? candidate
      : best
  );
  return `sep=${separator}\n${text}`;
}

export interface ISize {
  width: number;
  height: number;
}

/** The size that fits `max` on the long edge; never upscales, never below 1 px. */
export function scaleToLongEdge(
  width: number,
  height: number,
  max: number
): ISize {
  const longEdge = Math.max(width, height);
  if (longEdge <= max) return { width, height };
  const scale = max / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function cellText(cell: unknown): string {
  if (cell === null || cell === undefined) return '';
  return String(cell)
    .replace(/[\t\r\n]+/g, ' ')
    .trim();
}

/** `text` cut to `max` characters without splitting a surrogate pair. */
function cutText(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const last = cut.charCodeAt(cut.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
}

/**
 * A spreadsheet (SheetJS `sheet_to_json(ws, { header: 1, raw: false })`) as
 * TSV text: cells trimmed with their tabs and line breaks turned into spaces,
 * trailing empty cells and empty rows dropped, cut to the text limit.
 * SheetJS rows can be sparse arrays, so holes read as empty cells.
 */
export function sheetRowsToText(rows: unknown[][]): string {
  const lines: string[] = [];
  for (const row of rows) {
    if (!Array.isArray(row)) continue;
    const cells = Array.from(row, cellText);
    while (cells.length > 0 && cells[cells.length - 1] === '') cells.pop();
    if (cells.length > 0) lines.push(cells.join('\t'));
  }
  return cutText(lines.join('\n'), TEXT_CHARS_LIMIT);
}

export interface ICaptureCounts {
  /** Photo pages (a photo split in parts counts once). */
  photos: number;
  /** Every page (photos, PDF pages, sheets, pasted text; a page split in parts counts once). */
  pages: number;
  /** Every image and text page (each part of a page split in parts counts). */
  images: number;
}

/** A page (or part) that counts as a page of the owner: not a later part of a split page. */
export function countsAsPage(page: { originPart: number | null }): boolean {
  return page.originPart === null || page.originPart <= 1;
}

export type TLimitCheck =
  | 'ok'
  | 'tooManyPhotos'
  | 'tooManyPages'
  | 'tooManyImages';

/** Whether `adding` still fits next to the unread pages of the session (12 photos, 20 pages, 60 images). */
export function limitCheck(
  unread: ICaptureCounts,
  adding: ICaptureCounts
): TLimitCheck {
  if (unread.photos + adding.photos > PHOTOS_PER_READ) return 'tooManyPhotos';
  if (unread.pages + adding.pages > PAGES_PER_READ) return 'tooManyPages';
  if (unread.images + adding.images > IMAGES_PER_READ) return 'tooManyImages';
  return 'ok';
}

/**
 * The message of a read that is full: of photos or pages (how many fit), or
 * of images (long pages read in parts filled it: read what is there first).
 */
export function limitMessageKey(check: Exclude<TLimitCheck, 'ok'>): string {
  return check === 'tooManyImages'
    ? 'menuImport.errors.pageLimit'
    : `menuImport.tray.${check}`;
}
